import { useRef, useState } from 'react';

import { rpcClient } from '@/api/rpc/client';
import { analyzeDailyReportFile } from '@/lib/daily-reports/analyzeDailyReport';
import { enrichPotfUpcResults } from '@/lib/daily-reports/enrichPotfUpc';
import {
  mergeReportResults,
  summarizeDailyReportResults,
} from '@/lib/daily-reports/summarizeResults';
import type { DailyReportFileResult } from '@/lib/daily-reports/types';

/** A dropped file whose analysis hasn't finished yet. */
export interface PendingDailyReport {
  key: string;
  fileName: string;
}

/**
 * Resolves once the browser has painted. `.xls`/`.xlsx` parses hold the main
 * thread, so the pending rows have to be on screen before one starts.
 */
function afterNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });
}

/**
 * Holds dropped daily-report files and their analysis. Files are keyed by
 * a per-drop id so same-named exports both stay.
 */
export function useDailyReports() {
  const [results, setResults] = useState<DailyReportFileResult[]>([]);
  const [pending, setPending] = useState<PendingDailyReport[]>([]);
  const isAnalyzing = pending.length > 0;
  const generationRef = useRef(0);
  const summary = summarizeDailyReportResults(results);

  function invalidateInFlight(): void {
    generationRef.current += 1;
    setPending([]);
  }

  async function addFiles(files: File[]): Promise<void> {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setPending(
      files.map((file) => ({
        key: crypto.randomUUID(),
        fileName: file.name,
      })),
    );
    await afterNextPaint();
    const incoming = await Promise.all(files.map(analyzeDailyReportFile));
    if (generation !== generationRef.current) return;
    const enriched = await enrichPotfUpcResults(incoming, async (upcs) => {
      const response = await rpcClient.inventoryManagement.products.lookupByUpc(
        {
          upcs,
        },
      );
      return response.byUpc;
    });
    if (generation !== generationRef.current) return;
    setResults((current) => mergeReportResults(current, enriched));
    setPending([]);
  }

  function removeFile(id: string): void {
    setResults((current) => current.filter((result) => result.id !== id));
  }

  function clear(): void {
    invalidateInFlight();
    setResults([]);
  }

  return {
    results,
    summary,
    pending,
    isAnalyzing,
    addFiles,
    removeFile,
    clear,
  };
}
